import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { message } from 'antd';
import { useGetPublicAccountQuery } from '../../store/api/metricsApi';
import Button from '../../reusable/Button/Button';
import DataTable from '../../reusable/DataTable/DataTable';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import StatusChip from '../../reusable/StatusChip/StatusChip';
import SymbolFlag from '../../reusable/SymbolFlag/SymbolFlag';
import CertificateArt from '../PayoutCertificates/Certificates/CertificateArt';
import { usd } from '../../utilities/format';
import { isOffline } from '../../utilities/online';
import { t } from '../../i18n/i18n';
import { toShareView } from './data';
import './PublicProfile.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const toneOf = (value) => (String(value).trim().startsWith('-') ? 'down' : 'up');

/** Where a visitor goes when the page itself has nothing to show them. */
const SITE_URL = 'https://alphacapitalgroup.uk';

const PAYOUT_COLUMNS = [
  { key: 'requested', header: 'Requested on', width: 34 },
  { key: 'amount', header: 'Amount', width: 26 },
  {
    key: 'status',
    header: 'Status',
    width: 40,
    render: (row) => <StatusChip>{row.status}</StatusChip>,
  },
];

/**
 * What a failed load means to the person holding the link, in their words, not
 * the API's. English here, translated where it renders.
 *
 *   missing   a wrong or cut-off link, the wrong platform on it, or sharing
 *             turned off — the endpoint answers 404 to all three
 *   offline   the request never reached us
 *   busy      rate-limited
 *   server    anything else; worth another try
 */
const PROBLEMS = {
  missing: {
    icon: 'link-broken',
    tone: 'info',
    title: 'This performance page isn’t available',
    text: 'The link may be incomplete or mistyped, or the trader may have stopped sharing this account. Ask them to send the link again.',
  },
  offline: {
    icon: 'wifi-off',
    tone: 'warning',
    title: 'You’re offline',
    text: 'We couldn’t reach Alpha Capital. Check your internet connection and try again — the page will load as soon as you’re back.',
    retry: true,
  },
  busy: {
    icon: 'refresh',
    tone: 'warning',
    title: 'Too many requests right now',
    text: 'This page has been opened a lot in a short time. Wait a minute, then try again.',
    retry: true,
  },
  server: {
    icon: 'warning-triangle',
    tone: 'warning',
    title: 'We couldn’t load this page',
    text: 'Something went wrong on our side. It’s usually brief — please try again in a moment.',
    retry: true,
  },
};

/** RTK Query's error → one of PROBLEMS. A 200 with no account in it is a link to nothing. */
const problemOf = (error, data) => {
  if (!error) return data?.account ? null : 'missing';
  const { status } = error;
  if (status === 400 || status === 403 || status === 404) return 'missing';
  if (status === 429) return 'busy';
  if (status === 'FETCH_ERROR' || isOffline()) return 'offline';
  return 'server';
};

export default function PublicProfile() {
  const { accountId: pathId } = useParams();
  // The link carries the platform the login belongs to; MT5 is the default the
  // endpoint is queried with when a shared link was built without one.
  const [search] = useSearchParams();
  const { hash } = useLocation();

  /*
   * Links shared before the login was encoded split a TradeLocker login at its
   * "#": "/p/D#2568704?platform=tradelocker" reaches here as the path "D" and
   * the fragment "#2568704?platform=tradelocker". Put the login and the
   * platform back together from the two.
   */
  const [hashId, hashQuery] = hash ? hash.slice(1).split('?') : [];
  const accountId = hashId ? `${pathId}#${hashId}` : pathId;
  const platform =
    search.get('platform') || new URLSearchParams(hashQuery ?? '').get('platform') || 'mt5';

  // `currentData`, not `data`: `data` keeps the last account while another
  // link's is loading, and this page would show one trader's figures under
  // another's link.
  const { currentData: data, error, isLoading, isFetching, refetch } = useGetPublicAccountQuery({
    accountId,
    platform,
  });

  const {
    snapshotDate,
    accountName,
    accountMeta,
    headline,
    certificates,
    statistics,
    trades,
    payouts,
  } = useMemo(() => toShareView(data, t), [data]);
  const [preview, setPreview] = useState(null);

  // A retry clears the error before it answers, so "still waiting" is any
  // request in flight with nothing to show yet — the first load or a retry.
  const loading = !data && (isLoading || isFetching);
  const problem = loading ? null : problemOf(error, data);

  useEffect(() => {
    if (!preview) return undefined;
    const onKey = (e) => e.key === 'Escape' && setPreview(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  // The tab and a pasted link's preview name the account, not just the app.
  useEffect(() => {
    if (!data?.account) return undefined;
    const previous = document.title;
    document.title = `${accountName} · ${t('Verified by Alpha Capital')}`;
    return () => {
      document.title = previous;
    };
  }, [data, accountName]);

  if (loading) {
    return (
      <div className="public-profile">
        <TopBar />
        <PublicSkeleton />
      </div>
    );
  }

  if (problem) {
    const { icon: glyph, tone, title, text, retry } = PROBLEMS[problem];
    return (
      <div className="public-profile">
        <TopBar />
        <main className="pp-state-wrap">
          <section className={`pp-state pp-state--${tone}`} role="alert">
            <span className="pp-state__glyph">
              <span className="icon" style={icon(glyph)} />
            </span>
            <h1 className="pp-state__title">{t(title)}</h1>
            <p className="pp-state__text">{t(text)}</p>
            <div className="pp-state__actions">
              {/* "Start a challenge" is already in the bar above. */}
              {retry ? (
                <>
                  <button type="button" className="pp-cta pp-cta--lg" onClick={refetch}>
                    <span className="icon pp-cta__arrow" style={icon('refresh')} />
                    {t('Try again')}
                  </button>
                  <a className="pp-ghost" href={SITE_URL}>
                    {t('Visit Alpha Capital')}
                  </a>
                </>
              ) : (
                <a className="pp-cta pp-cta--lg" href={SITE_URL}>
                  {t('Visit Alpha Capital')}
                  <span className="icon pp-cta__arrow" style={icon('arrow-up-right')} />
                </a>
              )}
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="public-profile">
      <TopBar>
        <span className="pp-top__snapshot">{t('Snapshot')} · {snapshotDate}</span>
        <CopyToClipboard
          text={window.location.href}
          onCopy={(_text, ok) =>
            ok ? message.success(t('Link copied')) : message.error(t('Could not copy link'))
          }
        >
          <Button variant="toolbar" className="pp-copy">
            <span className="icon pp-copy__icon" style={icon('copy')} />
            {t('Copy link')}
          </Button>
        </CopyToClipboard>
      </TopBar>

      <main className="pp-main pp-main--in">
        <section className="pp-hero">
          <div className="pp-hero__profile">
            <span className="pp-hero__avatar">
              <span className="icon pp-hero__mark" style={icon('brand-mark')} />
            </span>
            <div className="pp-hero__foot">
              <div className="pp-hero__id">
                <span className="pp-hero__name">{accountName}</span>
                <span className="pp-hero__meta">{accountMeta}</span>
              </div>
              <span className="pp-readonly">{t('Read-only')}</span>
            </div>
          </div>

          {headline.map((stat) => (
            <div className="pp-stat" key={stat.label}>
              <div className="pp-stat__head">
                <span className="icon pp-stat__icon" style={icon(stat.icon)} />
                <span className="pp-stat__label">{t(stat.label)}</span>
              </div>
              <div className="pp-stat__body">
                <div className="pp-stat__value">
                  <span className="pp-stat__number">{stat.value}</span>
                  {stat.unit && <span className="pp-stat__unit">{stat.unit}</span>}
                </div>
                <p className="pp-stat__note">
                  {stat.note}
                  {stat.accent && (
                    <span className={`is-${stat.accentTone}`}>{stat.accent}</span>
                  )}
                  {stat.noteEnd}
                </p>
              </div>
            </div>
          ))}
        </section>

        {/* An account with no cleared payout has no certificates to march. */}
        {certificates.length > 0 && (
        <section className="pp-section">
          <div className="pp-sectionhead">
            <span className="pp-sectionhead__title">{t('Payout certificates')}</span>
            <span className="pp-sectionhead__note">
              {t('Verified payouts on this account. Hover for colour · click to enlarge.')}
            </span>
          </div>

          <div className="pp-certs">
            {/* The set is rendered twice so the marquee loops on a -50% shift. */}
            <div className="pp-certs__track">
              {[0, 1].map((copy) => (
                <div className="pp-certs__set" key={copy} aria-hidden={copy === 1}>
                  {certificates.map((item) => (
                    <button
                      type="button"
                      className="pp-cert"
                      key={`${copy}-${item.id}`}
                      onClick={() => setPreview(item)}
                    >
                      <CertificateArt item={item} className="pp-cert__art" loading="lazy" />
                      <span className="pp-cert__meta">
                        <span>{usd(item.amount, { decimals: 2 })}</span>
                        <span>{item.title}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

        <div className="pp-split">
          <section className="pp-card">
            <header className="pp-card__head">
              <span className="pp-card__title">
                <span className="icon pp-card__icon" style={icon('chart-profit')} />
                {t('Statistics')}
              </span>
              <span className="pp-muted">{statistics.note}</span>
            </header>

            <div className="pp-figures">
              {statistics.stats.map((stat) => (
                <div className="pp-figure" key={stat.label}>
                  <span className="pp-muted">{t(stat.label)}</span>
                  <span className={`pp-figure__value${stat.tone ? ` is-${stat.tone}` : ''}`}>
                    {stat.value}
                  </span>
                </div>
              ))}
            </div>

            <span className="pp-rule" />

            <header className="pp-card__head">
              <span className="pp-muted">{t('Volume by symbol')}</span>
              <span className="pp-muted">{t('Share of closed trades')}</span>
            </header>

            {statistics.symbols.length > 0 ? (
              <div className="pp-symbols">
                {statistics.symbols.map((row) => (
                  <div className="pp-symbol" key={row.symbol}>
                    <span className="pp-symbol__name">
                      <SymbolFlag symbol={row.symbol} />
                      {row.symbol}
                    </span>
                    <span className="pp-bar">
                      <span className="pp-bar__fill" style={{ width: `${row.pct}%` }} />
                    </span>
                    <span className="pp-symbol__pct">{row.pct}%</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="pp-empty">{t('No closed trades yet, so there’s no volume to show.')}</p>
            )}
          </section>

          <section className="pp-card">
            <header className="pp-card__head">
              <span className="pp-card__title">
                <span className="icon pp-card__icon" style={icon('nav-trades')} />
                {t('Recent trades')}
              </span>
              <span className="pp-muted">{t('Closed positions')}</span>
            </header>

            {trades.rows.length > 0 ? (
              <>
                <div className="pp-trades">
                  {trades.rows.map((row) => (
                    <div className="pp-trade" key={row.id}>
                      <span className="pp-trade__copy">
                        <span className="pp-trade__sym">
                          <SymbolFlag symbol={row.symbol} size={20} />
                          {row.symbol}
                        </span>
                        <span className="pp-muted">{row.detail}</span>
                      </span>
                      <span className="pp-trade__num">
                        <span className={`pp-trade__pnl is-${toneOf(row.pnl)}`}>{row.pnl}</span>
                        <span className="pp-muted">{row.meta}</span>
                      </span>
                    </div>
                  ))}
                </div>

                <footer className="pp-trades__foot">
                  <span className="pp-muted">{trades.shown}</span>
                  <span className={`is-${trades.netTone}`}>{trades.net}</span>
                </footer>
              </>
            ) : (
              <p className="pp-empty pp-empty--fill">
                {t('No closed trades yet. They’ll appear here once the first position is closed.')}
              </p>
            )}
          </section>
        </div>

        <section className="pp-section">
          <div className="pp-sectionhead">
            <span className="pp-sectionhead__title">{t('Payouts')}</span>
            <span className="pp-sectionhead__note">
              {t('Lifetime totals, then the three largest cleared withdrawals.')}
            </span>
          </div>

          <div className="pp-pulse">
            {payouts.pulse.map((item) => (
              <div className="pp-pulse__item" key={item.label}>
                <span className="pp-pulse__label">{t(item.label)}</span>
                <span className="pp-pulse__value">{item.value}</span>
              </div>
            ))}
          </div>

          {payouts.top.length > 0 && (
            <DataTable
              title="Top payouts"
              icon="nav-payout"
              columns={PAYOUT_COLUMNS}
              rows={payouts.top}
              minWidth={448}
              pagination={false}
            />
          )}
        </section>

        <footer className="pp-footer">
          <span>
            {t(
              'Shared by the account holder as a read-only snapshot. Email, login credentials, payment details and reference numbers are never part of a public performance page.'
            )}
          </span>
          <span className="pp-footer__fine">
            {t('Past performance is not indicative of future results. Trading involves risk of loss.')}
          </span>
        </footer>
      </main>

      {preview && (
        <div className="pp-modal" role="presentation" onClick={() => setPreview(null)}>
          <div className="pp-modal__panel" role="presentation" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="pp-modal__close"
              aria-label={t('Close certificate')}
              onClick={() => setPreview(null)}
            >
              ×
            </button>
            <CertificateArt item={preview} className="pp-modal__art" />
            <div className="pp-modal__meta">
              <span>{usd(preview.amount, { decimals: 2 })}</span>
              <span>{preview.title}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The bar every state shares. The "Verified" seal and the account's own
 * controls come in as `children` only once there is an account to vouch for —
 * a broken link shouldn't claim anything was verified.
 */
function TopBar({ children }) {
  return (
    <header className="pp-top">
      <a className="pp-top__home" href={SITE_URL} aria-label={t('Alpha Capital')}>
        <span className="icon pp-top__brand" style={icon('brand-alphacapital')} />
      </a>
      {children && (
        <span className="pp-verified">
          <span className="pp-verified__seal">
            <span className="icon" style={icon('brand-mark')} />
          </span>
          {t('Verified by Alpha Capital')}
        </span>
      )}

      <div className="pp-top__right">
        {children}
        <a className="pp-cta" href="/sign-up">
          {t('Start a challenge')}
          <span className="icon pp-cta__arrow" style={icon('arrow-up-right')} />
        </a>
      </div>
    </header>
  );
}

/**
 * The loaded page's own layout with bars in place of its values, so nothing
 * moves when the account arrives. Certificates are left out: most accounts
 * have none, and a strip that appears then vanishes is worse than one that
 * slides in.
 */
function PublicSkeleton() {
  return (
    <main className="pp-main" aria-busy="true" aria-label={t('Loading account…')}>
      <section className="pp-hero">
        <div className="pp-hero__profile">
          <Skeleton w={76} h={76} pill light />
          <div className="pp-hero__foot">
            <div className="pp-hero__id">
              <Skeleton w={150} h={18} light />
              <Skeleton w={210} h={10} light />
            </div>
            <Skeleton w={78} h={32} light />
          </div>
        </div>

        {[0, 1, 2].map((i) => (
          <div className="pp-stat" key={i}>
            <div className="pp-stat__head">
              <Skeleton w={14} h={14} radius={4} />
              <Skeleton w={70} h={10} />
            </div>
            <div className="pp-stat__body">
              <Skeleton w={i === 0 ? 150 : 96} h={36} />
              <Skeleton w={130} h={10} />
            </div>
          </div>
        ))}
      </section>

      <div className="pp-split">
        <section className="pp-card">
          <header className="pp-card__head">
            <Skeleton w={84} h={12} />
            <Skeleton w={110} h={10} />
          </header>
          <div className="pp-figures">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div className="pp-figure" key={i}>
                <Skeleton w={72} h={10} />
                <Skeleton w={i % 2 ? 44 : 62} h={12} />
              </div>
            ))}
          </div>
          <span className="pp-rule" />
          <header className="pp-card__head">
            <Skeleton w={96} h={10} />
            <Skeleton w={120} h={10} />
          </header>
          <div className="pp-symbols">
            {[72, 48, 30, 18].map((pct) => (
              <div className="pp-symbol" key={pct}>
                <span className="pp-symbol__name">
                  <Skeleton w={16} h={16} pill />
                  <Skeleton w={56} h={10} />
                </span>
                <span className="pp-bar">
                  <span className="pp-bar__fill pp-bar__fill--skel" style={{ width: `${pct}%` }} />
                </span>
                <Skeleton w={24} h={10} />
              </div>
            ))}
          </div>
        </section>

        <section className="pp-card">
          <header className="pp-card__head">
            <Skeleton w={96} h={12} />
            <Skeleton w={84} h={10} />
          </header>
          <div className="pp-trades">
            {[0, 1, 2, 3, 4].map((i) => (
              <div className="pp-trade" key={i}>
                <span className="pp-trade__copy">
                  <span className="pp-trade__sym">
                    <Skeleton w={20} h={20} pill />
                    <Skeleton w={58} h={12} />
                  </span>
                  <Skeleton w={120} h={10} />
                </span>
                <span className="pp-trade__num pp-trade__num--skel">
                  <Skeleton w={58} h={12} />
                  <Skeleton w={76} h={10} />
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="pp-section">
        <div className="pp-sectionhead">
          <Skeleton w={64} h={14} />
          <Skeleton w={260} h={10} />
        </div>
        <div className="pp-pulse">
          {[0, 1, 2, 3].map((i) => (
            <div className="pp-pulse__item" key={i}>
              <Skeleton w={96} h={10} />
              <Skeleton w={84} h={18} />
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
